<script setup>
/**
 * One app's hooks - the page behind every `/settings/integrations/:id`.
 *
 * Seven apps share this screen, and they split two ways. An app that allows
 * many hooks (Dialogflow, Travelport) is a list: the shell runs full width and
 * carries search, a count and `Add`. An app that allows one (Slack-style single
 * hooks) is a single card, so the shell stays in the narrow column and the card
 * owns its own connect/disconnect button.
 *
 * Deletion is confirmed here rather than in either child, because both of them
 * mean the same thing by it and the wording differs only by whether the hook is
 * attached to an inbox or to the account.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { isEmptyObject } from '../../../../helper/commons';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import NewHook from './NewHook.vue';
import SingleIntegrationHooks from './SingleIntegrationHooks.vue';
import MultipleIntegrationHooks from './MultipleIntegrationHooks.vue';

const props = defineProps({
  integrationId: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const {
  integration,
  isIntegrationMultiple,
  isIntegrationSingle,
  isHookTypeInbox,
} = useIntegrationHook(props.integrationId);

const uiFlags = useMapGetter('integrations/getUIFlags');
const globalConfig = useMapGetter('globalConfig/get');

const searchQuery = ref('');
const showAddHookModal = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedHook = ref({});

const showIntegrationHooks = computed(
  () => !uiFlags.value.isFetching && !isEmptyObject(integration.value)
);

const showAddButton = computed(
  () => showIntegrationHooks.value && isIntegrationMultiple.value
);

const hookCount = computed(() => integration.value.hooks?.length || 0);

// Only the multi-hook apps carry a blurb, and only once the record has landed -
// the key is built from the app's own name.
const description = computed(() => {
  if (!showIntegrationHooks.value || !isIntegrationMultiple.value) return '';
  return t(
    `INTEGRATION_APPS.SIDEBAR_DESCRIPTION.${integration.value.name.toUpperCase()}`,
    { installationName: globalConfig.value.installationName }
  );
});

const deleteTitle = computed(() =>
  isHookTypeInbox.value
    ? t('INTEGRATION_APPS.DELETE.TITLE.INBOX')
    : t('INTEGRATION_APPS.DELETE.TITLE.ACCOUNT')
);

const deleteMessage = computed(() =>
  isHookTypeInbox.value
    ? t('INTEGRATION_APPS.DELETE.MESSAGE.INBOX')
    : t('INTEGRATION_APPS.DELETE.MESSAGE.ACCOUNT')
);

const confirmText = computed(() =>
  isHookTypeInbox.value
    ? t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')
    : t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.ACCOUNT')
);

const openAddHookModal = () => {
  showAddHookModal.value = true;
};

const hideAddHookModal = () => {
  showAddHookModal.value = false;
};

const openDeletePopup = hook => {
  selectedHook.value = hook;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  let alertMessage = '';
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: selectedHook.value.id,
      appId: selectedHook.value.app_id,
    });
    alertMessage = t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE');
    closeDeletePopup();
  } catch (error) {
    alertMessage =
      error?.response?.data?.message ||
      t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE');
  } finally {
    useAlert(alertMessage);
  }
};

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsPageShell
    :wide="isIntegrationMultiple"
    :title="integration.name || ''"
    :description="description"
  >
    <template v-if="showAddButton" #actions>
      <span v-if="hookCount" class="text-sm text-n-slate-11">
        {{ $t('INTEGRATION_APPS.COUNT', { n: hookCount }) }}
      </span>
      <NextButton
        size="sm"
        :label="$t('INTEGRATION_APPS.ADD_BUTTON')"
        @click="openAddHookModal"
      />
    </template>

    <template v-if="showAddButton" #tabs>
      <SettingsSearchInput
        v-model="searchQuery"
        :placeholder="$t('INTEGRATION_APPS.SEARCH_PLACEHOLDER')"
      />
    </template>

    <SettingsSection
      v-if="uiFlags.isFetching"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_APPS.FETCHING')"
    >
      <div
        v-for="row in 4"
        :key="row"
        class="flex items-center gap-3 px-4 py-4"
      >
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <template v-else-if="showIntegrationHooks">
      <MultipleIntegrationHooks
        v-if="isIntegrationMultiple"
        v-model:search-query="searchQuery"
        :integration-id="integrationId"
        @delete="openDeletePopup"
      />

      <SingleIntegrationHooks
        v-if="isIntegrationSingle"
        :integration-id="integrationId"
        @add="openAddHookModal"
        @delete="openDeletePopup"
      />
    </template>
  </SettingsPageShell>

  <NewHook
    v-if="showAddHookModal"
    :integration-id="integrationId"
    @close="hideAddHookModal"
  />

  <ConfirmDialog
    v-model:show="showDeleteConfirmationPopup"
    :title="deleteTitle"
    :description="deleteMessage"
    :confirm-button-label="confirmText"
    :cancel-button-label="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    @confirm="confirmDeletion"
    @close="closeDeletePopup"
  />
</template>
